import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  Select,
  useToast,
  useColorModeValue,
  Grid,
  GridItem,
  IconButton,
  Badge,
  useBreakpointValue,
  Image,
  Center,
  Spinner
} from '@chakra-ui/react';
import { FiPlus, FiX, FiUpload, FiPlay, FiSave, FiTrash2 } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { getContentForEdit, updateContent } from '../services/contentUpdateService';
import { deleteContent } from '../services/contentDeletionService';
import { uploadFileDirect } from '../services/uploadService';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';

interface Episode {
  id: string;
  title: string;
  description: string;
  videoFile: File | null;
  videoPreview: string | null;
  existingVideoUrl?: string;
}

interface Season {
  id: string;
  title: string;
  description: string;
  episodes: Episode[];
}

interface SeriesFormData {
  title: string;
  description: string;
  genre: string;
  genres: string[];
  contentRating: string;
  posterFile: File | null;
  posterPreview: string | null;
  trailerFile: File | null;
  trailerPreview: string | null;
  seasons: Season[];
}

const EditSeries: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [series, setSeries] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [formData, setFormData] = useState<SeriesFormData>({
    title: '',
    description: '',
    genre: '',
    genres: [],
    contentRating: 'G',
    posterFile: null,
    posterPreview: null,
    trailerFile: null,
    trailerPreview: null,
    seasons: []
  });

  const [expandedSeasons, setExpandedSeasons] = useState<Set<string>>(new Set());

  const isMobile = useBreakpointValue({ base: true, md: false });
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.300');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const loadSeriesData = async () => {
    if (!id || !user) return;
    
    try {
      setLoading(true);
      const seriesData = await getContentForEdit(id, user.uid);
      
      if (seriesData) {
        setSeries(seriesData);
        
        // Convert existing seasons data to form format
        const existingSeasons: Season[] = ((seriesData as any).seasons || []).map((season: any, seasonIndex: number) => ({
          id: `season-${Date.now()}-${seasonIndex}`,
          title: season.title || '',
          description: season.description || '',
          episodes: (season.episodes || []).map((episode: any, episodeIndex: number) => ({
            id: `episode-${Date.now()}-${seasonIndex}-${episodeIndex}`,
            title: episode.title || '',
            description: episode.description || '',
            videoFile: null,
            videoPreview: null,
            existingVideoUrl: episode.videoUrl
          }))
        }));

        setFormData({
          title: seriesData.title || '',
          description: seriesData.description || '',
          genre: seriesData.genre || '',
          genres: seriesData.genres || [],
          contentRating: seriesData.contentRating || 'G',
          posterFile: null,
          posterPreview: seriesData.assets?.poster || seriesData.poster || '',
          trailerFile: null,
          trailerPreview: seriesData.assets?.trailer || seriesData.trailer || '',
          seasons: existingSeasons
        });

        // Auto-expand all existing seasons
        setExpandedSeasons(new Set(existingSeasons.map(s => s.id)));
      }
    } catch (error) {
      console.error('Error loading series data:', error);
      toast({ title: 'Error', description: 'Failed to load series data.', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSeriesData();
  }, [id, user]);

  const handleInputChange = (field: keyof SeriesFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenreToggle = (genre: string) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre]
    }));
  };

  const handlePosterSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        posterFile: file,
        posterPreview: url
      }));
    }
  };

  const handlePosterRemove = () => {
    setFormData(prev => ({
      ...prev,
      posterFile: null,
      posterPreview: null
    }));
  };

  const handleTrailerSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        trailerFile: file,
        trailerPreview: url
      }));
    }
  };

  const handleTrailerRemove = () => {
    setFormData(prev => ({
      ...prev,
      trailerFile: null,
      trailerPreview: null
    }));
  };

  const addSeason = () => {
    const newSeason: Season = {
      id: `season-${Date.now()}`,
      title: '',
      description: '',
      episodes: []
    };
    setFormData(prev => ({
      ...prev,
      seasons: [...prev.seasons, newSeason]
    }));
    setExpandedSeasons(prev => {
      const newSet = new Set(prev);
      newSet.add(newSeason.id);
      return newSet;
    });
  };

  const updateSeason = (seasonId: string, field: keyof Season, value: any) => {
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.map(season =>
        season.id === seasonId ? { ...season, [field]: value } : season
      )
    }));
  };

  const removeSeason = (seasonId: string) => {
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.filter(season => season.id !== seasonId)
    }));
    setExpandedSeasons(prev => {
      const newSet = new Set(prev);
      newSet.delete(seasonId);
      return newSet;
    });
  };

  const addEpisode = (seasonId: string) => {
    const newEpisode: Episode = {
      id: `episode-${Date.now()}`,
      title: '',
      description: '',
      videoFile: null,
      videoPreview: null
    };
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.map(season =>
        season.id === seasonId
          ? { ...season, episodes: [...season.episodes, newEpisode] }
          : season
      )
    }));
  };

  const updateEpisode = (seasonId: string, episodeId: string, field: keyof Episode, value: any) => {
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.map(season =>
        season.id === seasonId
          ? {
              ...season,
              episodes: season.episodes.map(episode =>
                episode.id === episodeId ? { ...episode, [field]: value } : episode
              )
            }
          : season
      )
    }));
  };

  const removeEpisode = (seasonId: string, episodeId: string) => {
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.map(season =>
        season.id === seasonId
          ? { ...season, episodes: season.episodes.filter(ep => ep.id !== episodeId) }
          : season
      )
    }));
  };

  const handleEpisodeVideoSelect = (seasonId: string, episodeId: string, file: File) => {
    const url = URL.createObjectURL(file);
    updateEpisode(seasonId, episodeId, 'videoFile', file);
    updateEpisode(seasonId, episodeId, 'videoPreview', url);
  };

  const toggleSeasonExpansion = (seasonId: string) => {
    setExpandedSeasons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(seasonId)) {
        newSet.delete(seasonId);
      } else {
        newSet.add(seasonId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    if (!user || !id) return;

    setSaving(true);
    setUploadProgress(0);

    try {
      // Upload poster
      let posterUrl = formData.posterPreview;
      if (formData.posterFile) {
        posterUrl = await uploadFileDirect(`series/${user.uid}/${id}/poster`, formData.posterFile);
        setUploadProgress(10);
      }

      // Upload trailer
      let trailerUrl = formData.trailerPreview;
      if (formData.trailerFile) {
        trailerUrl = await uploadFileDirect(`series/${user.uid}/${id}/trailer`, formData.trailerFile);
        setUploadProgress(20);
      }

      // Update series document
      const updateData = {
        title: formData.title,
        description: formData.description,
        genres: formData.genres,
        contentRating: formData.contentRating,
        posterFile: formData.posterFile || undefined,
        posterUrl: posterUrl,
        trailerFile: formData.trailerFile || undefined,
        trailerUrl: trailerUrl
      };

      await updateContent(id, user.uid, updateData);
      setUploadProgress(30);

      // Prepare seasons metadata
      const seasonsMetadata: Array<{
        seasonNumber: number;
        title: string;
        description: string;
        episodes: Array<{
          episodeNumber: number;
          title: string;
          description: string;
          videoUrl: string;
        }>;
      }> = [];

      // Upload episode videos and prepare metadata
      let totalProgress = 30;
      const progressIncrement = 60 / formData.seasons.length;

      for (let seasonIndex = 0; seasonIndex < formData.seasons.length; seasonIndex++) {
        const season = formData.seasons[seasonIndex];
        const seasonEpisodes: Array<{
          episodeNumber: number;
          title: string;
          description: string;
          videoUrl: string;
        }> = [];

        for (let episodeIndex = 0; episodeIndex < season.episodes.length; episodeIndex++) {
          const episode = season.episodes[episodeIndex];
          
          let videoUrl = episode.existingVideoUrl || '';
          if (episode.videoFile) {
            videoUrl = await uploadFileDirect(
              `series/${user.uid}/${id}/seasons/${seasonIndex + 1}/episodes/${episodeIndex + 1}`,
              episode.videoFile
            );
          }

          seasonEpisodes.push({
            episodeNumber: episodeIndex + 1,
            title: episode.title,
            description: episode.description,
            videoUrl: videoUrl
          });
        }

        seasonsMetadata.push({
          seasonNumber: seasonIndex + 1,
          title: season.title,
          description: season.description,
          episodes: seasonEpisodes
        });

        totalProgress += progressIncrement;
        setUploadProgress(Math.min(totalProgress, 90));
      }

      // Calculate total duration
      let totalDuration = 0;
      for (const season of seasonsMetadata) {
        for (const episode of season.episodes) {
          totalDuration += 45; // Default 45 minutes per episode
        }
      }

      // Update series document with seasons metadata
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../lib/firebase');
      await updateDoc(doc(db, 'content', id), {
        'assets.poster': posterUrl,
        'assets.trailer': trailerUrl,
        'assets.seasons': seasonsMetadata,
        'seasons': seasonsMetadata,
        'duration': totalDuration,
        'totalEpisodes': seasonsMetadata.reduce((total, season) => total + season.episodes.length, 0),
        'totalSeasons': seasonsMetadata.length
      });

      setUploadProgress(100);
      toast({ title: 'Series updated successfully!', status: 'success' });
      navigate('/series');
    } catch (error) {
      console.error('Error saving series:', error);
      toast({ title: 'Error saving series', description: 'Please try again.', status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSeries = async () => {
    if (!user || !id) return;

    if (window.confirm('Are you sure you want to delete this series? This action cannot be undone.')) {
      try {
        setSaving(true);
        await deleteContent(id, user.uid);
        
        toast({ title: 'Series deleted', description: 'Your series has been deleted.', status: 'success' });
        navigate('/series');
      } catch (error) {
        console.error('Error deleting series:', error);
        toast({ title: 'Error deleting series', description: 'Please try again.', status: 'error' });
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor} p={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch" maxW="1200px" mx="auto">
        {/* Header */}
        <HStack justify="space-between" align="center" wrap="wrap" gap={4}>
          <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={textColor}>
            Edit Series
          </Text>
          <HStack gap={2}>
            <Button
              leftIcon={<IconWrapper icon={FiSave} size={16} />}
              colorScheme="blue"
              onClick={handleSave}
              isLoading={saving}
              loadingText="Saving..."
              size={{ base: 'sm', md: 'md' }}
            >
              Save Changes
            </Button>
            <Button
              leftIcon={<IconWrapper icon={FiTrash2} size={16} />}
              colorScheme="red"
              variant="outline"
              onClick={handleDeleteSeries}
              isLoading={saving}
              size={{ base: 'sm', md: 'md' }}
            >
              Delete
            </Button>
          </HStack>
        </HStack>

        {/* Progress Bar */}
        {saving && (
          <Box>
            <Text fontSize="sm" color={labelColor} mb={2}>
              Saving series... {uploadProgress}%
            </Text>
            <Box w="100%" bg="gray.200" rounded="full" h="2">
              <Box
                bg="blue.500"
                h="100%"
                rounded="full"
                transition="width 0.3s ease"
                w={`${uploadProgress}%`}
              />
            </Box>
          </Box>
        )}

        <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={8}>
          {/* Series Information */}
          <VStack gap={6} align="stretch">
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              Series Information
            </Text>

            {/* Title */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Series Title *
              </FormLabel>
              <Input
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter series title"
                bg={inputBg}
                borderColor={borderColor}
              />
            </FormControl>

            {/* Description */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Description *
              </FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe your series"
                rows={4}
                bg={inputBg}
                borderColor={borderColor}
              />
            </FormControl>

            {/* Genres */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Genres *
              </FormLabel>
              <Box>
                {GENRES.map((genre) => (
                  <Button
                    key={genre}
                    size="sm"
                    variant={formData.genres.includes(genre) ? 'solid' : 'outline'}
                    colorScheme={formData.genres.includes(genre) ? 'purple' : 'gray'}
                    onClick={() => handleGenreToggle(genre)}
                    m={1}
                  >
                    {genre}
                  </Button>
                ))}
              </Box>
            </FormControl>

            {/* Content Rating */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Content Rating *
              </FormLabel>
              <Select
                value={formData.contentRating}
                onChange={(e) => handleInputChange('contentRating', e.target.value)}
                bg={inputBg}
                borderColor={borderColor}
              >
                {CONTENT_RATINGS.map((rating) => (
                  <option key={rating.value} value={rating.value}>
                    {rating.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Poster Upload */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Series Poster *
              </FormLabel>
              <Box
                border="2px dashed"
                borderColor={formData.posterPreview ? 'purple.300' : borderColor}
                borderRadius="lg"
                p={6}
                textAlign="center"
                bg={formData.posterPreview ? 'purple.50' : bgColor}
                _dark={{ bg: formData.posterPreview ? 'purple.900' : 'gray.600' }}
                cursor="pointer"
                _hover={{ borderColor: 'purple.400' }}
                transition="all 0.2s"
                onClick={() => document.getElementById('poster-upload')?.click()}
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {formData.posterPreview ? (
                  <VStack gap={3}>
                    <Image
                      src={formData.posterPreview}
                      alt="Poster preview"
                      maxH="150px"
                      maxW="150px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                    <Text fontSize="sm" color={textColor}>
                      Click to change poster
                    </Text>
                    <IconButton
                      aria-label="Remove poster"
                      icon={<IconWrapper icon={FiX} size={16} />}
                      size="sm"
                      colorScheme="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePosterRemove();
                      }}
                    />
                  </VStack>
                ) : (
                  <VStack gap={3}>
                    <IconWrapper icon={FiUpload} size={32} color="gray.400" />
                    <Text color={textColor} fontWeight="medium">
                      Click to upload poster
                    </Text>
                    <Text fontSize="sm" color={labelColor}>
                      JPG, PNG up to 10MB
                    </Text>
                  </VStack>
                )}
              </Box>
              <Input
                id="poster-upload"
                type="file"
                accept="image/*"
                onChange={handlePosterSelect}
                display="none"
              />
            </FormControl>

            {/* Trailer Upload */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Series Trailer (Optional)
              </FormLabel>
              <Box
                border="2px dashed"
                borderColor={formData.trailerPreview ? 'purple.300' : borderColor}
                borderRadius="lg"
                p={6}
                textAlign="center"
                bg={formData.trailerPreview ? 'purple.50' : bgColor}
                _dark={{ bg: formData.trailerPreview ? 'purple.900' : 'gray.600' }}
                cursor="pointer"
                _hover={{ borderColor: 'purple.400' }}
                transition="all 0.2s"
                onClick={() => document.getElementById('trailer-upload')?.click()}
                minH="120px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {formData.trailerPreview ? (
                  <VStack gap={3}>
                    <Box position="relative">
                      <IconWrapper icon={FiPlay} size={32} color="purple.500" />
                      <Text fontSize="sm" color={textColor} fontWeight="medium">
                        {formData.trailerFile?.name || 'Trailer uploaded'}
                      </Text>
                      <IconButton
                        aria-label="Remove trailer"
                        icon={<IconWrapper icon={FiX} size={16} />}
                        size="sm"
                        colorScheme="red"
                        position="absolute"
                        top="-8px"
                        right="-8px"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTrailerRemove();
                        }}
                      />
                    </Box>
                    <Text fontSize="sm" color={textColor}>
                      Click to change trailer
                    </Text>
                  </VStack>
                ) : (
                  <VStack gap={3}>
                    <IconWrapper icon={FiPlay} size={32} color="gray.400" />
                    <Text color={textColor} fontWeight="medium">
                      Click to upload trailer
                    </Text>
                    <Text fontSize="sm" color={labelColor}>
                      MP4, MOV up to 100MB
                    </Text>
                  </VStack>
                )}
              </Box>
              <Input
                id="trailer-upload"
                type="file"
                accept="video/*"
                onChange={handleTrailerSelect}
                display="none"
              />
            </FormControl>
          </VStack>

          {/* Seasons and Episodes */}
          <VStack gap={6} align="stretch">
            <HStack justify="space-between" align="center">
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                Seasons & Episodes
              </Text>
              <Button
                leftIcon={<IconWrapper icon={FiPlus} size={16} />}
                colorScheme="purple"
                variant="outline"
                onClick={addSeason}
                size="sm"
              >
                Add Season
              </Button>
            </HStack>

            {formData.seasons.map((season, seasonIndex) => (
              <Box key={season.id} p={4} border="1px solid" borderColor={borderColor} borderRadius="lg">
                <VStack gap={4} align="stretch">
                  {/* Season Header */}
                  <HStack justify="space-between" align="center">
                    <HStack gap={2} align="center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleSeasonExpansion(season.id)}
                        p={1}
                        minW="auto"
                      >
                        <IconWrapper 
                          icon={expandedSeasons.has(season.id) ? FiX : FiPlus} 
                          size={16} 
                        />
                      </Button>
                      <Text fontWeight="bold" color={textColor}>
                        Season {seasonIndex + 1}
                        {season.title && `: ${season.title}`}
                      </Text>
                      <Badge colorScheme="purple" variant="subtle">
                        {season.episodes.length} episodes
                      </Badge>
                    </HStack>
                    <IconButton
                      aria-label="Remove season"
                      icon={<IconWrapper icon={FiX} size={16} />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => removeSeason(season.id)}
                    />
                  </HStack>

                  {/* Collapsible Season Content */}
                  {expandedSeasons.has(season.id) && (
                    <VStack gap={4} align="stretch">
                      {/* Season Title */}
                      <Input
                        value={season.title}
                        onChange={(e) => updateSeason(season.id, 'title', e.target.value)}
                        placeholder="Season title"
                        bg={inputBg}
                        borderColor={borderColor}
                      />

                      {/* Season Description */}
                      <Textarea
                        value={season.description}
                        onChange={(e) => updateSeason(season.id, 'description', e.target.value)}
                        placeholder="Season description"
                        bg={inputBg}
                        borderColor={borderColor}
                        rows={2}
                      />

                      {/* Episodes */}
                      <VStack gap={3} align="stretch">
                        <HStack justify="space-between" align="center">
                          <Text fontSize="sm" fontWeight="medium" color={labelColor}>
                            Episodes ({season.episodes.length})
                          </Text>
                          <Button
                            leftIcon={<IconWrapper icon={FiPlus} size={14} />}
                            size="xs"
                            colorScheme="purple"
                            variant="outline"
                            onClick={() => addEpisode(season.id)}
                          >
                            Add Episode
                          </Button>
                        </HStack>

                        {season.episodes.map((episode, episodeIndex) => (
                          <Box key={episode.id} p={3} bg={inputBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
                            <VStack gap={3} align="stretch">
                              <HStack justify="space-between" align="center">
                                <Text fontSize="sm" fontWeight="medium" color={textColor}>
                                  Episode {episodeIndex + 1}
                                </Text>
                                <IconButton
                                  aria-label="Remove episode"
                                  icon={<IconWrapper icon={FiX} size={14} />}
                                  size="xs"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => removeEpisode(season.id, episode.id)}
                                />
                              </HStack>

                              <Input
                                value={episode.title}
                                onChange={(e) => updateEpisode(season.id, episode.id, 'title', e.target.value)}
                                placeholder="Episode title"
                                size="sm"
                                bg={bgColor}
                                borderColor={borderColor}
                              />

                              <Textarea
                                value={episode.description}
                                onChange={(e) => updateEpisode(season.id, episode.id, 'description', e.target.value)}
                                placeholder="Episode description"
                                size="sm"
                                bg={bgColor}
                                borderColor={borderColor}
                                rows={2}
                              />

                              {/* Video Upload */}
                              <Box
                                border="2px dashed"
                                borderColor={episode.videoFile || episode.existingVideoUrl ? 'purple.300' : borderColor}
                                borderRadius="md"
                                p={4}
                                textAlign="center"
                                bg={episode.videoFile || episode.existingVideoUrl ? 'purple.50' : bgColor}
                                _dark={{ bg: episode.videoFile || episode.existingVideoUrl ? 'purple.900' : 'gray.600' }}
                                cursor="pointer"
                                _hover={{ borderColor: 'purple.400' }}
                                transition="all 0.2s"
                                onClick={() => document.getElementById(`video-upload-${episode.id}`)?.click()}
                                minH="80px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                {episode.videoFile || episode.existingVideoUrl ? (
                                  <VStack gap={2}>
                                    <Text fontSize="sm" color={textColor} fontWeight="medium">
                                      {episode.videoFile?.name || 'Video uploaded'}
                                    </Text>
                                    <Text fontSize="xs" color={labelColor}>
                                      Click to change video
                                    </Text>
                                  </VStack>
                                ) : (
                                  <VStack gap={2}>
                                    <IconWrapper icon={FiUpload} size={24} color="gray.400" />
                                    <Text fontSize="xs" color={textColor}>
                                      Click to upload video
                                    </Text>
                                  </VStack>
                                )}
                              </Box>
                              <Input
                                id={`video-upload-${episode.id}`}
                                type="file"
                                accept="video/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleEpisodeVideoSelect(season.id, episode.id, file);
                                  }
                                }}
                                display="none"
                              />
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    </VStack>
                  )}
                </VStack>
              </Box>
            ))}

            {formData.seasons.length === 0 && (
              <Box
                p={8}
                textAlign="center"
                border="2px dashed"
                borderColor={borderColor}
                borderRadius="lg"
                bg={inputBg}
              >
                <Text color={labelColor} mb={4}>
                  No seasons added yet
                </Text>
                <Button
                  leftIcon={<IconWrapper icon={FiPlus} size={16} />}
                  colorScheme="purple"
                  variant="outline"
                  onClick={addSeason}
                >
                  Add First Season
                </Button>
              </Box>
            )}
          </VStack>
        </Grid>
      </VStack>
    </Box>
  );
};

export default EditSeries;
